/**
 * Modern Cart Functionality
 * Handles all cart interactions including quantity updates, item removal, and checkout
 */

class ModernCart {
  constructor() {
    this.elements = {
      cartForm: document.getElementById('cart-form'),
      cartItems: document.querySelector('.modern-cart-items'),
      orderSummary: document.querySelector('.modern-order-summary'),
      subtotalElement: document.querySelector('.order-details-row.subtotal span:last-child'),
      totalElement: document.querySelector('.order-details-row.total .price'),
      itemCount: document.querySelector('.item-count'),
    };
    
    this.init();
  }

  init() {
    this.bindQuantityButtons();
    this.bindRemoveButtons();
    this.bindCheckoutButton();
    this.setupCartUpdateListeners();
    this.initializePriceFormats();
  }

  /**
   * Bind quantity increase/decrease buttons
   */
  bindQuantityButtons() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('.quantity-button')) {
        e.preventDefault();
        const button = e.target;
        const isPlus = button.classList.contains('plus');
        const input = button.parentElement.querySelector('input');
        const currentValue = parseInt(input.value) || 0;
        const itemKey = button.dataset.itemKey || input.dataset.key;
        
        let newQuantity;
        if (isPlus) {
          newQuantity = currentValue + 1;
        } else {
          newQuantity = Math.max(0, currentValue - 1);
        }
        
        // Update the input value immediately for better UX
        input.value = newQuantity;
        
        // Update cart via AJAX
        this.updateCartQuantity(itemKey, newQuantity, button.closest('.modern-cart-item'));
      }
    });

    // Handle direct input changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('.item-quantity input')) {
        const input = e.target;
        const itemKey = input.dataset.key || input.closest('.modern-cart-item').dataset.key;
        const newQuantity = Math.max(0, parseInt(input.value) || 0);
        
        // Store original value before update
        input.setAttribute('data-original-value', input.defaultValue);
        
        this.updateCartQuantity(itemKey, newQuantity, input.closest('.modern-cart-item'));
      }
    });
    
    // Store initial values
    document.querySelectorAll('.item-quantity input').forEach(input => {
      input.defaultValue = input.value;
      input.setAttribute('data-original-value', input.value);
    });
  }

  /**
   * Update cart quantity via Shopify AJAX API
   */
  async updateCartQuantity(itemKey, quantity, itemElement) {
    try {
      console.log('Updating cart:', { itemKey, quantity });
      
      // Show loading state
      this.setLoadingState(itemElement, true);
      
      // Get the line number from the element ID (1-based)
      const line = parseInt(itemElement.id.replace('CartItem-', ''));
      console.log('Using line number:', line);
      
      // Use the same format as the theme's cart.js
      const body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: [],
        sections_url: window.location.pathname
      });
      
      const response = await fetch(`${window.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      });

      console.log('Cart update response:', response.status);

      const responseText = await response.text();
      const state = JSON.parse(responseText);
      
      if (state.errors) {
        console.error('Cart update error:', state.errors);
        alert(state.errors);
        // Revert the quantity
        const input = itemElement.querySelector('.item-quantity input');
        if (input) {
          input.value = input.getAttribute('data-original-value') || input.defaultValue;
        }
        throw new Error(state.errors);
      }

      console.log('Cart updated successfully:', state);
      
      // Update the cart UI with the state
      this.updateCartUI(state);
      
      // Handle item removal
      if (quantity === 0) {
        itemElement.style.animation = 'fadeOut 0.3s ease';
        setTimeout(() => {
          itemElement.remove();
          // Re-index remaining items after removal
          this.reindexCartItems();
          // Check if cart is now empty - show empty state without reloading
          const remainingItems = document.querySelectorAll('.modern-cart-item');
          if (remainingItems.length === 0) {
            this.showEmptyCartState();
          }
        }, 300);
      } else {
        // Update the line item price with dynamic format switching
        const lineIndex = line - 1; // Convert to 0-based index
        if (state.items && state.items[lineIndex]) {
          const item = state.items[lineIndex];
          
          // Update price format for both desktop and mobile
          this.updateItemPriceFormat(itemElement, item);
          
          // Update the quantity input to reflect any inventory limits
          const input = itemElement.querySelector('.item-quantity input');
          if (input && item.quantity !== quantity) {
            input.value = item.quantity;
            input.setAttribute('data-original-value', item.quantity);
          }
        }
      }
      
      // Re-index all items to ensure line numbers are correct
      this.reindexCartItems();
      
    } catch (error) {
      console.error('Error updating cart:', error);
      // Revert the quantity input
      const input = itemElement.querySelector('.item-quantity input');
      if (input) {
        const originalValue = input.getAttribute('data-original-value') || input.defaultValue;
        input.value = originalValue;
      }
      alert('Failed to update cart. Please try again.');
    } finally {
      this.setLoadingState(itemElement, false);
    }
  }


  /**
   * Bind remove buttons (desktop and mobile)
   */
  bindRemoveButtons() {
    document.addEventListener('click', async (e) => {
      if (e.target.matches('.item-actions button[name="remove"], .item-actions button[name="remove"] *, .mobile-remove-btn, .mobile-remove-btn *')) {
        e.preventDefault();
        const button = e.target.closest('button');
        const cartItem = button.closest('.modern-cart-item');
        const itemKey = cartItem.dataset.key || button.dataset.key || cartItem.querySelector('.quantity-button').dataset.itemKey;

        if (confirm('Are you sure you want to remove this item?')) {
          await this.updateCartQuantity(itemKey, 0, cartItem);
        }
      }
    });
  }

  /**
   * Bind checkout button
   */
  bindCheckoutButton() {
    const checkoutBtn = document.querySelector('.checkout-button');
    if (checkoutBtn) {
      // Store original button content
      const originalContent = checkoutBtn.innerHTML;

      checkoutBtn.addEventListener('click', () => {
        // Disable button to prevent double clicks
        checkoutBtn.disabled = true;
        checkoutBtn.innerHTML = 'Processing...';

        // Redirect to Shopify checkout
        window.location.href = '/checkout';
      });

      // Reset button state when user navigates back to page
      window.addEventListener('pageshow', (event) => {
        if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
          checkoutBtn.disabled = false;
          checkoutBtn.innerHTML = originalContent;
        }
      });
    }
  }




  /**
   * Update cart UI after changes
   */
  async updateCartUI(state) {
    // Update subtotal and total in the order summary
    const formattedSubtotal = this.formatMoney(state.original_total_price || state.total_price);
    const formattedTotal = this.formatMoney(state.total_price);
    
    // Update subtotal (use original price before discounts)
    const subtotalElements = document.querySelectorAll('.order-details-row.subtotal span:last-child, .subtotal .order-details-row span:last-child');
    subtotalElements.forEach(element => {
      element.textContent = formattedSubtotal;
    });
    
    // Update total (final price after discounts)
    const totalElements = document.querySelectorAll('.order-details-row.total .price, .total .price');
    totalElements.forEach(element => {
      element.textContent = formattedTotal;
    });
    
    // Update item count in cart header
    if (this.elements.itemCount) {
      const itemText = state.item_count === 1 ? 'item' : 'items';
      this.elements.itemCount.textContent = `(${state.item_count} ${itemText})`;
    }
    
    
    // Update header cart count (cart drawer bubble)
    const cartCountElements = document.querySelectorAll('.cart-count-bubble, [data-cart-count], .cart-count');
    cartCountElements.forEach(element => {
      element.textContent = state.item_count;
      if (element.hasAttribute('data-cart-count')) {
        element.setAttribute('data-cart-count', state.item_count);
      }
    });
    
    // Update header cart total with USD currency
    const headerCartTotalElements = document.querySelectorAll('[id^="cart-icon-bubble"] .cart-count-bubble-header span.visually-hiddenn');
    headerCartTotalElements.forEach(element => {
      // Use currency format for header cart total only
      const currencyFormat = window.theme.moneyWithCurrencyFormat || window.theme.moneyFormat;
      element.textContent = this.formatMoney(state.total_price, currencyFormat);
    });
    
    // Update other cart totals with regular format (cart page, etc.)
    const otherCartTotalElements = document.querySelectorAll('.cart-total, [data-cart-total]');
    otherCartTotalElements.forEach(element => {
      element.textContent = formattedTotal;
    });
    
    // Update shipping display based on cart total
    this.updateShippingDisplay(state.total_price);
    
    // Update free shipping tracker
    this.updateFreeShippingTracker(state.total_price);
    
    // Trigger cart update events for other components
    document.dispatchEvent(new CustomEvent('cart:updated', { 
      detail: { cart: state } 
    }));
    
    // Update any Shopify cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.querySelector('.cart-count-bubble')) {
      cartDrawer.querySelector('.cart-count-bubble').textContent = state.item_count;
    }
    
    // If cart is empty, show empty state without reloading
    if (state.item_count === 0 && !document.querySelector('.modern-cart-empty')) {
      this.showEmptyCartState();
    }
    
    console.log('Cart UI updated:', { 
      itemCount: state.item_count, 
      subtotal: formattedSubtotal,
      total: formattedTotal 
    });
  }

  /**
   * Update free shipping tracker
   */
  updateFreeShippingTracker(totalPrice) {
    const tracker = document.querySelector('.free-shipping-tracker');
    if (!tracker) return;
    
    const freeShippingThreshold = 5000; // $50.00 in cents
    const remainingAmount = freeShippingThreshold - totalPrice;
    
    if (totalPrice >= freeShippingThreshold) {
      tracker.innerHTML = '<span class="free-shipping-achieved">🚚 You\'ve earned FREE shipping!</span>';
    } else {
      const formattedRemaining = this.formatMoney(remainingAmount);
      tracker.innerHTML = `
        <span class="free-shipping-progress">
          <span class="remaining-amount">${formattedRemaining}</span> away from FREE Shipping 
          <a href="/collections/all" class="shop-more-link">Shop more</a>
        </span>
      `;
    }
  }

  /**
   * Update shipping display based on cart total
   */
  updateShippingDisplay(totalPrice) {
    const shippingRow = document.querySelector('.order-details-row.shipping span:last-child');
    if (shippingRow) {
      const freeShippingThreshold = 5000; // $50.00 in cents
      
      if (totalPrice >= freeShippingThreshold) {
        shippingRow.innerHTML = '<span class="shipping-free">FREE</span>';
        shippingRow.className = ''; // Remove any existing classes
        shippingRow.querySelector('.shipping-free').style.color = '#52c41a';
        shippingRow.querySelector('.shipping-free').style.fontWeight = '500';
      } else {
        shippingRow.textContent = 'Calculated in Checkout';
        shippingRow.className = '';
        shippingRow.style.color = '#666';
        shippingRow.style.fontWeight = 'normal';
      }
    }
  }

  /**
   * Show empty cart state without page reload
   */
  showEmptyCartState() {
    const modernCart = document.querySelector('.modern-cart');
    if (modernCart) {
      modernCart.innerHTML = `
        <div class="modern-cart-empty">
          <h2>Your cart is empty</h2>
          <a href="/collections/all" class="button">
            Continue shopping
          </a>
        </div>
      `;
    }
  }

  /**
   * Re-index cart items to maintain correct line numbers
   */
  reindexCartItems() {
    const cartItems = document.querySelectorAll('.modern-cart-item');
    cartItems.forEach((item, index) => {
      const newLineNumber = index + 1;
      const oldId = item.id;
      const newId = `CartItem-${newLineNumber}`;
      
      // Update the element ID
      item.id = newId;
      
      // Update the quantity input ID
      const quantityInput = item.querySelector('.item-quantity input');
      if (quantityInput) {
        quantityInput.id = `Quantity-${newLineNumber}`;
        quantityInput.setAttribute('data-index', newLineNumber);
      }
      
      console.log(`Reindexed item from ${oldId} to ${newId}`);
    });
  }

  /**
   * Format money according to shop settings
   */
  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    // Fallback to simple formatter
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Set loading state for an element
   */
  setLoadingState(element, isLoading) {
    if (isLoading) {
      element.style.opacity = '0.6';
      element.style.pointerEvents = 'none';
    } else {
      element.style.opacity = '1';
      element.style.pointerEvents = 'auto';
    }
  }

  /**
   * Initialize price formats for all cart items on page load
   */
  async initializePriceFormats() {
    try {
      // Get current cart state
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update format for each item
      const cartItems = document.querySelectorAll('.modern-cart-item');
      cartItems.forEach((itemElement, index) => {
        if (cart.items && cart.items[index]) {
          this.updateItemPriceFormat(itemElement, cart.items[index]);
        }
      });
    } catch (error) {
      console.error('Error initializing price formats:', error);
    }
  }

  /**
   * Update item price format based on quantity (single vs multi)
   */
  updateItemPriceFormat(itemElement, item) {
    // Find all price containers for this item (both desktop and mobile)
    const priceContainers = itemElement.querySelectorAll('.item-price');
    
    // Detect discounts from existing HTML elements (more reliable than API data)
    const hasComparePrice = itemElement.querySelector('.compare-price') !== null;
    const hasOriginalPrice = itemElement.querySelector('.original-price') !== null;
    
    const isOnSale = hasComparePrice || hasOriginalPrice;
    
    
    // Get original prices from HTML if they exist
    let originalPrice = '';
    let salePrice = '';
    
    if (hasComparePrice) {
      const comparePriceEl = itemElement.querySelector('.compare-price');
      originalPrice = comparePriceEl ? comparePriceEl.textContent.trim() : '';
    } else if (hasOriginalPrice) {
      const originalPriceEl = itemElement.querySelector('.original-price');
      originalPrice = originalPriceEl ? originalPriceEl.textContent.trim() : '';
    }
    
    const salePriceEl = itemElement.querySelector('.sale-price');
    salePrice = salePriceEl ? salePriceEl.textContent.trim() : this.formatMoney(item.final_price || item.price);
    
    priceContainers.forEach(priceContainer => {
      if (item.quantity === 1) {
        // Quantity = 1: Show single price format
        if (isOnSale) {
          this.showSingleDiscountedFormat(priceContainer, { ...item, originalPrice, salePrice });
        } else {
          this.showSingleRegularFormat(priceContainer, { ...item, salePrice });
        }
      } else {
        // Quantity > 1: Show unit price + subtotal format
        if (isOnSale) {
          this.showMultiDiscountedFormat(priceContainer, { ...item, originalPrice, salePrice });
        } else {
          this.showMultiRegularFormat(priceContainer, { ...item, salePrice });
        }
      }
    });
  }

  /**
   * Show single price format for DISCOUNTED items (quantity = 1)
   */
  showSingleDiscountedFormat(priceContainer, item) {
    // Hide unit price and subtotal elements
    const unitPriceElements = priceContainer.querySelectorAll('.unit-price');
    const subtotalElements = priceContainer.querySelectorAll('.item-subtotal');
    
    unitPriceElements.forEach(el => el.style.display = 'none');
    subtotalElements.forEach(el => el.style.display = 'none');
    
    // Show or create single price element
    let singlePriceContainer = priceContainer.querySelector('.single-price');
    if (!singlePriceContainer) {
      singlePriceContainer = document.createElement('div');
      singlePriceContainer.className = 'single-price';
      priceContainer.appendChild(singlePriceContainer);
    }
    
    singlePriceContainer.style.display = 'flex';
    singlePriceContainer.innerHTML = `
      <div class="compare-price">${item.originalPrice}</div>
      <div class="sale-price" style="color: rgb(254, 104, 70) !important;">${item.salePrice}</div>
    `;
  }

  /**
   * Show single price format for REGULAR items (quantity = 1)
   */
  showSingleRegularFormat(priceContainer, item) {
    // Hide unit price and subtotal elements
    const unitPriceElements = priceContainer.querySelectorAll('.unit-price');
    const subtotalElements = priceContainer.querySelectorAll('.item-subtotal');
    
    unitPriceElements.forEach(el => el.style.display = 'none');
    subtotalElements.forEach(el => el.style.display = 'none');
    
    // Show or create single price element
    let singlePriceContainer = priceContainer.querySelector('.single-price');
    if (!singlePriceContainer) {
      singlePriceContainer = document.createElement('div');
      singlePriceContainer.className = 'single-price';
      priceContainer.appendChild(singlePriceContainer);
    }
    
    singlePriceContainer.style.display = 'flex';
    singlePriceContainer.innerHTML = `
      <div class="sale-price" style="color: #000 !important;">${item.salePrice}</div>
    `;
  }

  /**
   * Show multi price format for DISCOUNTED items (quantity > 1)
   * Shows only unit price, no "ea" label or subtotal
   */
  showMultiDiscountedFormat(priceContainer, item) {
    // Hide unit price and subtotal elements
    const unitPriceElements = priceContainer.querySelectorAll('.unit-price');
    const subtotalElements = priceContainer.querySelectorAll('.item-subtotal');

    unitPriceElements.forEach(el => el.style.display = 'none');
    subtotalElements.forEach(el => el.style.display = 'none');

    // Show or create single price element (same format as qty=1)
    let singlePriceContainer = priceContainer.querySelector('.single-price');
    if (!singlePriceContainer) {
      singlePriceContainer = document.createElement('div');
      singlePriceContainer.className = 'single-price';
      priceContainer.appendChild(singlePriceContainer);
    }

    singlePriceContainer.style.display = 'flex';
    singlePriceContainer.innerHTML = `
      <div class="compare-price">${item.originalPrice}</div>
      <div class="sale-price" style="color: rgb(254, 104, 70) !important;">${item.salePrice}</div>
    `;
  }

  /**
   * Show multi price format for REGULAR items (quantity > 1)
   * Shows only unit price, no "ea" label or subtotal
   */
  showMultiRegularFormat(priceContainer, item) {
    // Hide unit price and subtotal elements
    const unitPriceElements = priceContainer.querySelectorAll('.unit-price');
    const subtotalElements = priceContainer.querySelectorAll('.item-subtotal');

    unitPriceElements.forEach(el => el.style.display = 'none');
    subtotalElements.forEach(el => el.style.display = 'none');

    // Show or create single price element (same format as qty=1)
    let singlePriceContainer = priceContainer.querySelector('.single-price');
    if (!singlePriceContainer) {
      singlePriceContainer = document.createElement('div');
      singlePriceContainer.className = 'single-price';
      priceContainer.appendChild(singlePriceContainer);
    }

    singlePriceContainer.style.display = 'flex';
    singlePriceContainer.innerHTML = `
      <div class="sale-price" style="color: #000 !important;">${item.salePrice}</div>
    `;
  }

  /**
   * Setup cart update listeners for other cart modifications
   */
  setupCartUpdateListeners() {
    // Listen for cart updates from other sources
    document.addEventListener('cart:updated', async () => {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateCartUI(cart);
    });
  }

  /**
   * Save for later functionality (placeholder)
   */
  bindSaveForLater() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('.save-for-later')) {
        e.preventDefault();
        const variantId = e.target.dataset.variantId;
        // This would typically integrate with a wishlist or saved items feature
        alert('Save for later functionality would be implemented here');
      }
    });
  }
}

// Add fadeOut animation
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeOut {
    from { opacity: 1; transform: translateX(0); }
    to { opacity: 0; transform: translateX(-20px); }
  }
`;
document.head.appendChild(style);

// Initialize cart when DOM is ready (only if cart is not empty)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('.modern-cart-empty')) {
      new ModernCart();
    }
  });
} else {
  if (!document.querySelector('.modern-cart-empty')) {
    new ModernCart();
  }
}